import type { Quest } from '../../../shared/CharacterTypes';
import { getQuest } from '../../../shared/content/generated/quests.gen';
import { questObjectiveTargetName } from '../../../shared/quests/questObjectiveDisplay';
import { resolveQuestEndNpcDisplay } from '../../../shared/quests/questEndNpcDisplay';
import { questRewardsFor } from '../../../shared/quests/questRewardItems';
import type { QuestProgressWire } from '../../network/ClientMessageTypes';

/**
 * Build a React Quest entry from a quest id + its wire progress. Resolves the
 * static def from QUEST_DATABASE and attaches per-objective progress (multi-
 * objective quests) from the wire `counts` (falling back to single `progress`).
 * Returns null for unknown ids. (2026-06-28 — multi-objective chain support.)
 */
export function toReactQuest(id: string, v: QuestProgressWire | undefined, heroClass?: string | null): Quest | null {
  const def = getQuest(id);
  if (!def) return null;
  const counts = Array.isArray(v?.counts) ? v!.counts : (v?.progress != null ? [v.progress] : []);
  const defObjs = def.objectives;
  const endNpc = resolveQuestEndNpcDisplay(def.endNpc);
  const objectives = defObjs.map((o, i) => ({
    type: o.type,
    targetId: o.targetId,
    targetName: questObjectiveTargetName(o.type ?? 'explore', o.targetId ?? '', o.targetCount ?? 1),
    description: o.description,
    targetCount: o.targetCount ?? 1,
    currentCount: counts[i] ?? 0,
  }));
  const firstObj = defObjs[0] ?? null;
  // This owner snapshot has no current-NPC context. Talk objectives remain
  // active here; the destination NPC dialogue marks them actionable when the
  // player reaches the authored contact.
  const readyToTurnIn = defObjs.length > 0 && defObjs.every((o, i) =>
    (counts[i] ?? 0) >= (o.targetCount ?? 1));
  return {
    ...def,
    // Shared items plus this class's class_items entry (lane AO).
    rewards: questRewardsFor(def, heroClass),
    endNpcId: endNpc.npcId ?? undefined,
    endNpcName: endNpc.name,
    targetId: firstObj?.targetId ?? '',
    targetCount: firstObj?.targetCount ?? 1,
    currentCount: counts[0] ?? 0,
    objectives: objectives.length ? objectives : undefined,
    // Legacy field name: active quest surfaces use this as ready-to-turn-in,
    // while permanent completion lives in QuestLog.completed.
    completed: readyToTurnIn,
  };
}
